import { PORTAL_ROLES } from './navPermissions';
import { isCrmRole } from './crmPermissions';

function portalAttendancePath(roleSlug) {
  return roleSlug === 'parent' ? '/portal/parent/attendance' : '/portal/student/attendance';
}

function portalExamsPath(roleSlug) {
  return roleSlug === 'parent' ? '/portal/parent/exams' : '/portal/student/exams';
}

function portalLeavePath(roleSlug) {
  return roleSlug === 'parent' ? '/portal/parent/leave' : '/portal/student/leave';
}

function portalFeesPath(roleSlug) {
  return roleSlug === 'parent' ? '/portal/parent/fees' : '/portal/student/fees';
}

function portalHomeworkPath(roleSlug) {
  return roleSlug === 'parent' ? '/portal/parent/homework' : '/portal/student/homework';
}

function splitPathAndSearch(link) {
  const qIdx = link.indexOf('?');
  if (qIdx === -1) return { pathname: link, search: '' };
  return { pathname: link.slice(0, qIdx), search: link.slice(qIdx) };
}

function parseAttendancePeriodLabel(title) {
  const raw = String(title || '');
  const parts = raw.split(/\s*[—–-]\s*/);
  if (parts.length < 2) return null;
  const label = parts[parts.length - 1].trim();
  return label || null;
}

function appendQueryParam(pathWithSearch, key, value) {
  if (!value) return pathWithSearch;
  const joiner = pathWithSearch.includes('?') ? '&' : '?';
  return `${pathWithSearch}${joiner}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

function inferFeeReceiptFallback(roleSlug, paymentId) {
  const base = portalFeesPath(roleSlug);
  if (roleSlug === 'parent' || !paymentId) {
    return base;
  }
  return appendQueryParam(base, 'payment_id', paymentId);
}

function inferLinkFromNotification(notification, roleSlug) {
  const title = String(notification?.title || '').toLowerCase();
  const isParent = roleSlug === 'parent';
  const paymentId = String(notification?.payment_id || '').trim();

  if (title.startsWith('absent:')) {
    return portalAttendancePath(roleSlug);
  }
  if (title.includes('attendance report')) {
    const base = portalAttendancePath(roleSlug);
    const periodLabel = parseAttendancePeriodLabel(notification?.title);
    return periodLabel ? `${base}?period_label=${encodeURIComponent(periodLabel)}` : base;
  }
  if (title.startsWith('event:')) {
    return portalAttendancePath(roleSlug);
  }
  if (title.startsWith('announcement:')) {
    return '/dashboard';
  }
  if (title.startsWith('exam datesheet:') || title.startsWith('exam results:')) {
    return portalExamsPath(roleSlug);
  }
  if (title.includes('report card')) {
    return isParent ? '/dashboard' : '/portal/student/report-card';
  }
  if (title.includes('homework') || title.includes('assignment')) {
    return portalHomeworkPath(roleSlug);
  }
  if (title.includes('leave')) {
    return portalLeavePath(roleSlug);
  }
  if (title.includes('fee received')) {
    return inferFeeReceiptFallback(roleSlug, paymentId);
  }
  if (title.includes('fee')) {
    return inferFeeReceiptFallback(roleSlug, paymentId);
  }

  if (title.includes('follow-up due')) {
    return '/crm/inquiries';
  }
  if (title.includes('new field inquiry')) {
    const inquiryId = String(notification?.inquiry_id || notification?.entity_id || '').trim();
    if (inquiryId) {
      return `/crm/inquiries/${inquiryId}`;
    }
    const link = String(notification?.link || '').trim();
    if (link.startsWith('/crm/inquiries/')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    return '/crm/inquiries';
  }
  if (title.includes('new portal registration')) {
    const schoolId = String(notification?.school_id || notification?.entity_id || '').trim();
    if (schoolId) {
      return `/schools/${schoolId}`;
    }
    const link = String(notification?.link || '').trim();
    if (link.startsWith('/schools/')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    return '/schools';
  }
  if (title.includes('new web inquiry')) {
    const inquiryId = String(notification?.inquiry_id || notification?.entity_id || '').trim();
    if (inquiryId) {
      return `/crm/inquiries/${inquiryId}`;
    }
    const link = String(notification?.link || '').trim();
    if (link.startsWith('/crm/inquiries/')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    return '/crm/inquiries';
  }
  if (title.includes('new school joined')) {
    return '/crm/inquiries';
  }
  if (title.includes('salary generated') || title.includes('salaries generated')
    || title.includes('payroll generated') || title.includes('payroll approved')
    || title.includes('salary paid') || title.includes('payslip')) {
    if (title.includes('payslip')) {
      return '/crm/salaries?tab=payslips';
    }
    return '/crm/salaries';
  }

  if (title.includes('ticket assigned') || title.includes('ticket status') || title.includes('ticket comment')
    || title.includes('ticket priority') || title.includes('ticket due date') || title.includes('ticket completed')) {
    const link = String(notification?.link || '').trim();
    if (link.startsWith('/crm/tickets/')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    const ticketId = String(notification?.ticket_id || notification?.entity_id || '').trim();
    if (ticketId) {
      return `/crm/tickets/${ticketId}`;
    }
    return '/crm/tickets';
  }

  if (title.includes('new email') || title.includes('email send failed') || title.includes('queued email sent') || title.includes('email sent:')) {
    const link = String(notification?.link || '').trim();
    if (link.startsWith('/crm/mail')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    return '/crm/mail';
  }

  if (title.includes('new crm message')) {
    const chatUserId = String(notification?.chat_user_id || '').trim();
    if (chatUserId) {
      return `/crm/dashboard?crm_chat_user=${encodeURIComponent(chatUserId)}`;
    }
    const link = String(notification?.link || '').trim();
    if (link.includes('crm_chat_user=')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    return '/crm/dashboard';
  }

  if (title.includes('new feedback submitted')) {
    const link = String(notification?.link || '').trim();
    if (link.startsWith('/school-feedback/')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    if (link.startsWith('/feedback/')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    if (roleSlug === 'school_admin' || roleSlug === 'principal') {
      return '/school-feedback';
    }
    return '/feedback';
  }
  if (title.includes('feedback update') || title.includes('reply to your feedback')) {
    const link = String(notification?.link || '').trim();
    if (link.startsWith('/my-feedback/')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    return '/my-feedback';
  }
  if (title.includes('feedback assigned to you')) {
    const link = String(notification?.link || '').trim();
    if (link.startsWith('/crm/feedback/')) {
      return link.startsWith('/#/') ? link.slice(2) : link;
    }
    if (link.startsWith('/feedback/')) {
      const normalized = link.startsWith('/#/') ? link.slice(2) : link;
      return normalized.replace(/^\/feedback\//, '/crm/feedback/');
    }
    return '/crm/feedback';
  }

  if (isCrmRole(roleSlug)) {
    return '/crm/dashboard';
  }

  if (PORTAL_ROLES.includes(roleSlug)) {
    return portalAttendancePath(roleSlug);
  }

  return '/dashboard';
}

function remapPortalLink(pathname, roleSlug) {
  const isParent = roleSlug === 'parent';

  if (pathname.startsWith('/portal/')) {
    return pathname;
  }
  if (pathname === '/events' || pathname.includes('/calendar')) {
    return portalAttendancePath(roleSlug);
  }
  if (pathname === '/announcements') {
    return '/dashboard';
  }
  if (pathname === '/exams' || pathname.startsWith('/exams/')) {
    return portalExamsPath(roleSlug);
  }
  if (pathname.startsWith('/reports/report-card')) {
    return isParent ? '/dashboard' : '/portal/student/report-card';
  }
  if (pathname === '/leave') {
    return portalLeavePath(roleSlug);
  }
  if (pathname.startsWith('/fees')) {
    return portalFeesPath(roleSlug);
  }
  if (pathname === '/assignments') {
    return portalHomeworkPath(roleSlug);
  }

  return pathname;
}

/**
 * Resolve a notification link for the current user (portal remaps + title fallbacks).
 */
export function resolveNotificationLink(user, notification, fallbackPath = '/dashboard') {
  const roleSlug = user?.role_slug || '';
  const effectiveFallback = (fallbackPath === '/dashboard' && isCrmRole(roleSlug))
    ? '/crm/dashboard'
    : fallbackPath;
  const isPortal = PORTAL_ROLES.includes(roleSlug);

  let raw = String(notification?.link || '').trim();
  if (raw.startsWith('/#/')) raw = raw.slice(2);

  const paymentId = String(notification?.payment_id || '').trim();

  if (!raw && paymentId) {
    const title = String(notification?.title || '').toLowerCase();
    if (title.includes('fee received') || title.includes('fee')) {
      return inferFeeReceiptFallback(roleSlug, paymentId);
    }
  }

  if (!raw) {
    return inferLinkFromNotification(notification, roleSlug) || effectiveFallback;
  }

  const { pathname: rawPath, search: rawSearch } = splitPathAndSearch(raw);
  if (rawPath === '/invoice-portal' || rawPath.startsWith('/invoice-portal')) {
    return rawPath + rawSearch;
  }

  if (!isPortal) {
    return raw || effectiveFallback;
  }

  const { pathname, search } = splitPathAndSearch(raw);
  const remapped = remapPortalLink(pathname, roleSlug);
  let resolved = remapped + search;

  // Preserve homework deep-link params when remapping staff paths to portal homework.
  if (
    remapped.includes('/homework')
    && search
    && !resolved.includes('assignment_id=')
    && search.includes('assignment_id=')
  ) {
    resolved = remapped + search;
  }

  if (
    remapped.includes('/attendance')
    && !resolved.includes('report_id=')
    && !resolved.includes('period_label=')
    && String(notification?.title || '').toLowerCase().includes('attendance report')
  ) {
    const periodLabel = parseAttendancePeriodLabel(notification?.title);
    if (periodLabel) {
      resolved = appendQueryParam(resolved, 'period_label', periodLabel);
    }
  }

  return resolved;
}
